/**
 * authService — network calls for auth, wired to the key derivation layer.
 *
 * Only this file talks to the network for auth. Components and stores call this,
 * never fetch() directly. The password never leaves this module — only derived
 * key material is sent to the server.
 */

import { KeyManager, type PersistedKeyStore, keyManager } from './keyManager'
import { type EncryptedBlob, toBase64Url, base32ToBytes, unformatRecoveryCode } from './crypto'
import { secureStorage } from './secureStorage'
import { withRequestDeadline, throwIfAborted } from './requestDeadline'

const DEFAULT_API_URL = 'https://api.superelectric.ink'
const _apiBase = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) || DEFAULT_API_URL
const API_BASE: string = _apiBase.replace(/\/$/, '')

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AuthSession {
  accessToken: string
  refreshToken: string
  sessionId?: string
  userId: string
  workspaceId?: string
  preparedKeys?: PersistedKeyStore
}

interface LoginResponse {
  accessToken: string
  refreshToken: string
  sessionId?: string
  userId: string
  workspaceId?: string
  encryptedMasterKey: EncryptedBlob
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export class AuthHttpError extends Error {
  constructor(message: string, public status: number, public reason?: string) {
    super(message)
    this.name = 'AuthHttpError'
  }
}

async function request<T>(path: string, init: RequestInit, signal?: AbortSignal): Promise<T> {
  return withRequestDeadline(async (requestSignal) => {
    const res = await fetch(`${API_BASE}${path}`, { ...init, credentials: 'include', signal: requestSignal })
    if (!res.ok) {
      const body: unknown = await res.json().catch(() => null)
      const err = body && typeof body === 'object' ? body as { error?: unknown; reason?: unknown } : {}
      throw new AuthHttpError(
        typeof err.error === 'string' ? err.error : `HTTP ${res.status}`,
        res.status,
        typeof err.reason === 'string' ? err.reason : undefined,
      )
    }
    return await res.json() as T
  }, { signal })
}

function post<T>(path: string, body: unknown, token?: string, signal?: AbortSignal): Promise<T> {
  return request(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  }, signal)
}

function get<T>(path: string, token?: string, customHeaders?: Record<string, string>, signal?: AbortSignal): Promise<T> {
  return request(path, { method: 'GET', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...customHeaders } }, signal)
}

function put<T>(path: string, body: unknown, token?: string): Promise<T> {
  return request(path, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(body) })
}

function patch<T>(path: string, body: unknown, token?: string): Promise<T> {
  return request(path, { method: 'PATCH', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(body) })
}

function del<T>(path: string, token?: string): Promise<T> {
  return request(path, { method: 'DELETE', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } })
}

function delWithBody<T>(path: string, body: unknown, token: string): Promise<T> {
  return request(path, { method: 'DELETE', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) })
}

interface KdfParamsResponse {
  kdfNonce: string
  argon2: { memorySize: number; iterations: number; parallelism: number; hashLength: number }
}

/**
 * Fetches the per-account KDF nonce for `username`, ahead of running Argon2id.
 * Always succeeds with a 200 — for not-yet-registered usernames the server
 * returns a deterministic decoy nonce, so this call alone never reveals
 * whether an account exists (see worker/src/auth.ts's GET /auth/params).
 *
 * Recovery key material is intentionally excluded. It is released only after
 * the client proves possession of the recovery credential to
 * POST /auth/recovery/start.
 */
async function fetchKdfParams(username: string, signal?: AbortSignal): Promise<KdfParamsResponse> {
  return get<KdfParamsResponse>(`/auth/params?username=${encodeURIComponent(username.toLowerCase().trim())}`, undefined, undefined, signal)
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Logs in and unlocks the key session.
 * After this call, keyManager holds the MEK in memory.
 *
 * Argon2id runs ONCE here (heavy). The derived rootKey is reused for the
 * server auth step and for unlocking the local key session.
 */
export async function login(
  username: string,
  password: string,
  deviceLabel = 'Web',
  signal?: AbortSignal,
): Promise<AuthSession> {
  // Fetch this account's KDF nonce BEFORE running Argon2id — this is the
  // network round-trip that makes offline bulk precomputation infeasible.
  const { kdfNonce } = await fetchKdfParams(username, signal)

  // Derive the root key ONCE — used for both auth and session unlock
  const candidate = new KeyManager()
  const rootKey = await candidate.deriveRootKey(password, username, kdfNonce)
  const { authKeyRaw, kek } = await candidate.deriveKeySet(rootKey)
  rootKey.fill(0)

  // Encode auth key as base64url for the wire, then zero it
  const authKeyHash = toBase64Url(authKeyRaw)
  authKeyRaw.fill(0)

  // Authenticate — server returns encrypted key material
  const res = await post<LoginResponse>('/auth/login', { username, authKeyHash, deviceLabel }, undefined, signal)
  throwIfAborted(signal)

  // Unlock the key session using the already-derived keys
  // (avoids running Argon2id a second time)
  const preparedKeys = await candidate.prepareSessionFromKeys(kek, {
    encryptedMasterKey: res.encryptedMasterKey,
  }, res.userId, kdfNonce, signal)

  preparedKeys.offlineUnlock = { username: username.toLowerCase().trim(), kdfNonce, encryptedMasterKey: res.encryptedMasterKey }

  return {
    preparedKeys,
    accessToken: res.accessToken,
    refreshToken: res.refreshToken,
    sessionId: res.sessionId,
    userId: res.userId,
    workspaceId: res.workspaceId,
  }
}

/**
 * Resets a forgotten password using the recovery code generated at
 * registration. Decrypts the MEK locally with the recovery code, re-wraps
 * the SAME MEK under a KEK derived from the new password, and authorizes the
 * server-side update with a hash derived from the recovery code — the
 * recovery code itself is never sent over the network, mirroring how the
 * password itself never leaves the client during a normal login.
 */
export async function resetPasswordWithRecoveryCode(
  username: string,
  recoveryCode: string,
  newPassword: string,
): Promise<void> {
  const normalisedUsername = username.toLowerCase().trim()
  const { kdfNonce } = await fetchKdfParams(normalisedUsername)

  // Recompute the recovery auth key hash the server can verify against the
  // one it stored at registration — proves possession of the recovery code
  // without ever transmitting the code itself.
  const recoveryCodeBytes = base32ToBytes(unformatRecoveryCode(recoveryCode))
  const { recoveryAuthKeyRaw } = await keyManager.deriveRecoveryKeySet(recoveryCodeBytes, false)
  recoveryCodeBytes.fill(0)
  const recoveryAuthKeyHash = toBase64Url(recoveryAuthKeyRaw)
  recoveryAuthKeyRaw.fill(0)

  // The public KDF endpoint deliberately does not expose recovery key
  // material. Release the wrapped MEK only after the server has verified the
  // recovery credential (with the same rate limits and indistinguishable
  // failure response used by the reset itself).
  const { recoveryWrappedMasterKey } = await post<{
    recoveryWrappedMasterKey: EncryptedBlob
  }>('/auth/recovery/start', {
    username: normalisedUsername,
    recoveryAuthKeyHash,
  })

  const existingMasterKey = await keyManager.unwrapMasterKeyWithRecoveryCode(
    recoveryCode,
    recoveryWrappedMasterKey,
  )

  const resetPayload = await keyManager.buildRekeyPayload(
    newPassword,
    normalisedUsername,
    kdfNonce,
    existingMasterKey,
  )

  await post('/auth/recovery/reset', {
    username: normalisedUsername,
    recoveryAuthKeyHash,
    newAuthKeyHash: resetPayload.authKeyHash,
    newEncryptedMasterKey: resetPayload.encryptedMasterKey,
  })
}

/**
 * Requests a "forgot username" reminder email. Always resolves without
 * revealing whether the email matched an account — the server intentionally
 * returns the same { ok: true } response either way, so this endpoint can't
 * be used to enumerate registered email addresses.
 */
export async function requestUsernameReminder(email: string): Promise<void> {
  await post<{ ok: boolean }>('/auth/forgot-username', { email: email.toLowerCase().trim() })
}

// ── Authenticated rekey (password / username change) ──────────────────────

export interface RekeyResult {
  /** The (possibly unchanged) username after the rekey. */
  username: string
  /** The kdf_nonce now in effect for `username`. */
  kdfNonce: string
}

/**
 * Shared implementation behind changePassword and changeUsername.
 *
 * Both are cryptographically the same operation — derive a new KEK from
 * (newPassword ?? currentPassword, newUsername ?? currentUsername, a fresh
 * kdfNonce) and re-wrap the SAME MEK under it — because the username feeds
 * the Argon2id salt (see keyManager.ts's file header), so a username-only
 * change is a full rekey too. Both therefore require the CURRENT password,
 * re-derived against the CURRENT username/kdfNonce, as proof of the current
 * credential — the server only ever sees derived hashes, never the password.
 */
async function rekey(
  accessToken: string,
  currentPassword: string,
  newUsername?: string,
  newPassword?: string,
): Promise<RekeyResult> {
  const candidate = new KeyManager()
  // a. Resolve the CURRENT username and kdfNonce.
  const currentUsername = await secureStorage.getUsername()
  if (!currentUsername) {
    throw new Error('Username not found. Please sign in again.')
  }
  const currentKdfNonce = keyManager.getCachedKdfNonce() || (await secureStorage.getKdfNonce())
  if (!currentKdfNonce) {
    throw new Error('Missing session parameters. Please sign in again.')
  }

  // b. Target username is the new one if we're changing it, else unchanged —
  // fetch ITS kdfNonce fresh from the server (never trust a client-cached
  // value for the target, and never trust a client-supplied one for a brand
  // new username).
  const targetUsername = (newUsername ?? currentUsername).toLowerCase().trim()
  const { kdfNonce: newKdfNonce } = await fetchKdfParams(targetUsername)

  // c. Re-derive the CURRENT root key from the just-entered current password,
  // against the CURRENT username/kdfNonce. authKeyRaw proves the current
  // credential; the KEK opens the server-stored MEK lockbox. The MEK itself
  // must come from that lockbox, because it intentionally remains stable when
  // the password or username changes and therefore may no longer equal the
  // masterKey output of deriveKeySet.
  const currentRootKey = await candidate.deriveRootKey(currentPassword, currentUsername, currentKdfNonce)
  const { authKeyRaw: currentAuthKeyRaw, kek: currentKek } = await candidate.deriveKeySet(currentRootKey, true)
  currentRootKey.fill(0)
  const currentAuthKeyHash = toBase64Url(currentAuthKeyRaw)
  currentAuthKeyRaw.fill(0)

  const { encryptedMasterKey } = await get<{ encryptedMasterKey: EncryptedBlob | string }>(
    '/auth/key-material',
    accessToken,
  )
  const existingMasterKey = await candidate.unwrapMasterKeyWithKek(currentKek, encryptedMasterKey)

  // d. Re-wrap the SAME MEK under the new credential — never generates a new MEK.
  const effectiveNewPassword = newPassword ?? currentPassword
  const rekeyPayload = await candidate.buildRekeyPayload(
    effectiveNewPassword,
    targetUsername,
    newKdfNonce,
    existingMasterKey,
  )

  // e. PUT to the endpoint matching what's changing.
  const endpoint = newUsername ? '/auth/username' : '/auth/password'
  await put(endpoint, {
    currentAuthKeyHash,
    newAuthKeyHash: rekeyPayload.authKeyHash,
    newEncryptedMasterKey: rekeyPayload.encryptedMasterKey,
    ...(newUsername ? { newUsername: targetUsername } : {}),
  }, accessToken)

  // Update this device's offline password proof after successful rotation.
  // A failed local write leaves the previous local password usable; it never deletes keys.
  try {
    await keyManager.updateOfflineUnlock({ username: targetUsername, kdfNonce: newKdfNonce, encryptedMasterKey: rekeyPayload.encryptedMasterKey })
  } catch {
    throw new Error('Your cloud credentials changed, but offline unlock could not be updated on this device. Keep using the previous local password to unlock, free storage space, then sign in online with your new credentials.')
  }
  return { username: targetUsername, kdfNonce: newKdfNonce }
}

/**
 * Changes the account password. Requires the CURRENT password (never the
 * recovery code). Re-wraps the existing MEK under a KEK derived from the new
 * password; never generates a new MEK. Revokes every refresh token for the
 * account server-side, forcing re-authentication on all other devices.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
  accessToken: string,
): Promise<RekeyResult> {
  return rekey(accessToken, currentPassword, undefined, newPassword)
}

/**
 * Changes the account username. Still requires the CURRENT password, even
 * though the password itself isn't changing — the username is baked into
 * the Argon2id salt (SHA256(username:kdfNonce)), so this is cryptographically
 * a full rekey, not a metadata update. Re-wraps the existing MEK; never
 * generates a new one. Revokes every refresh token for the account
 * server-side, forcing re-authentication on all other devices.
 */
export async function changeUsername(
  currentPassword: string,
  newUsername: string,
  accessToken: string,
): Promise<RekeyResult> {
  return rekey(accessToken, currentPassword, newUsername)
}

/**
 * Replaces the account recovery code while preserving the existing MEK.
 * The current password is re-derived locally to open an extractable copy of
 * the MEK from the server lockbox and to prove the current credential.
 */
export async function rotateRecoveryCode(
  currentPassword: string,
  accessToken: string,
): Promise<string> {
  const currentUsername = await secureStorage.getUsername()
  if (!currentUsername) {
    throw new Error('Username not found. Please sign in again.')
  }
  const currentKdfNonce = keyManager.getCachedKdfNonce() || (await secureStorage.getKdfNonce())
  if (!currentKdfNonce) {
    throw new Error('Missing session parameters. Please sign in again.')
  }

  const rootKey = await keyManager.deriveRootKey(currentPassword, currentUsername, currentKdfNonce)
  const { authKeyRaw, kek } = await keyManager.deriveKeySet(rootKey, true)
  rootKey.fill(0)
  const currentAuthKeyHash = toBase64Url(authKeyRaw)
  authKeyRaw.fill(0)

  // Read the password-wrapped MEK rather than assuming it is still identical
  // to the password-derived key (it intentionally stays stable across password changes).
  const { encryptedMasterKey } = await get<{ encryptedMasterKey: EncryptedBlob | string }>(
    '/auth/key-material',
    accessToken,
  )
  const existingMasterKey = await keyManager.unwrapMasterKeyWithKek(kek, encryptedMasterKey)
  const replacement = await keyManager.buildRecoveryRotationPayload(existingMasterKey)
  await put('/auth/recovery-key', {
    currentAuthKeyHash,
    newRecoveryAuthKeyHash: replacement.recoveryAuthKeyHash,
    newRecoveryWrappedMasterKey: replacement.recoveryWrappedMasterKey,
  }, accessToken)

  return replacement.recoveryCode
}

export interface RefreshResponse {
  accessToken: string
  refreshToken?: string
  sessionId?: string
  userId?: string
  workspaceId?: string
}

/**
 * Exchanges a refresh token for a new access token.
 * On web, the HttpOnly cookie is sent automatically; on desktop/mobile the
 * raw refreshToken string is passed explicitly.
 */
export async function refreshAccessToken(refreshToken?: string, signal?: AbortSignal): Promise<RefreshResponse> {
  const res = await post<RefreshResponse>('/auth/refresh', { refreshToken }, undefined, signal)
  if (!res || typeof res.accessToken !== 'string' || !res.accessToken.trim()) {
    throw new Error('Malformed refresh response')
  }
  return res
}

/** Revokes only the remote session. Local keys are owned by explicit local actions. */
export async function logout(accessToken: string, refreshToken?: string): Promise<void> {
  await post('/auth/logout', { refreshToken }, accessToken)
}

/** Permanently deletes only the signed-in account's server-side D1, KV, and R2 data. */
export async function deleteAccount(
  currentPassword: string,
  confirmationUsername: string,
  accessToken: string,
): Promise<void> {
  const currentUsername = await secureStorage.getUsername()
  const currentKdfNonce = keyManager.getCachedKdfNonce() || (await secureStorage.getKdfNonce())
  if (!currentUsername || !currentKdfNonce) {
    throw new Error('Missing session parameters. Please sign in again.')
  }

  const rootKey = await keyManager.deriveRootKey(currentPassword, currentUsername, currentKdfNonce)
  const { authKeyRaw } = await keyManager.deriveKeySet(rootKey)
  rootKey.fill(0)
  const currentAuthKeyHash = toBase64Url(authKeyRaw)
  authKeyRaw.fill(0)

  await delWithBody('/api/account', {
    currentAuthKeyHash,
    confirmationUsername,
  }, accessToken)
}

// ── Session & Device Management ──────────────────────────────────────────────

export interface DeviceSession {
  id: string
  deviceId?: string | null
  deviceLabel: string
  createdAt: string
  lastUsedAt: string | null
  expiresAt: string
  isCurrentSession?: boolean
}

/**
 * Lists all active device sessions for the authenticated user.
 */
export async function listSessions(
  accessToken: string,
  options?: { sessionId?: string; refreshToken?: string },
): Promise<DeviceSession[]> {
  const headers: Record<string, string> = {}
  if (options?.sessionId) headers['X-Session-Id'] = options.sessionId
  if (options?.refreshToken) headers['X-Refresh-Token'] = options.refreshToken
  const res = await get<{ sessions: DeviceSession[] }>('/api/sessions', accessToken, headers)
  return res.sessions
}

/**
 * Revokes an individual device session by ID.
 */
export async function revokeSession(sessionId: string, accessToken: string): Promise<void> {
  await del(`/api/sessions/${encodeURIComponent(sessionId)}`, accessToken)
}

/**
 * Renames a device session.
 */
export async function renameSession(
  sessionId: string,
  newLabel: string,
  accessToken: string,
): Promise<string> {
  const res = await patch<{ ok: boolean; deviceLabel: string }>(
    `/api/sessions/${encodeURIComponent(sessionId)}`,
    { deviceLabel: newLabel },
    accessToken,
  )
  return res.deviceLabel
}
